import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const VietBacLessonPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Breadcrumbs */}
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-red-600 transition-colors">Trang chủ</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/van" className="hover:text-red-600 transition-colors">Văn học</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium">Phân tích bài thơ Việt Bắc</span>
          </div>
        </div>

        {/* Article Header */}
        <article className="max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Merriweather, serif' }}>
              Phân Tích Bài Thơ Việt Bắc - Tố Hữu
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
              <span className="bg-red-100 text-red-700 px-3 py-1.5 rounded-full font-medium">
                <i className="ri-book-open-line mr-1"></i>
                Văn học lớp 12
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                15 phút đọc
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                12,450 lượt xem
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-folder-line"></i>
                Văn học hiện đại
              </span>
            </div>

            <div className="h-96 rounded-2xl overflow-hidden mb-8">
              <img 
                src="https://public.readdy.ai/ai/img_res/3bef84d1809cb6ed8db68e6b900f76b4.jpg"
                alt="Bài thơ Việt Bắc"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Table of Contents */}
          <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8 mb-12 border border-red-100">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-list-check text-red-600"></i>
              Mục lục
            </h3>
            <ul className="space-y-2.5">
              <li>
                <a href="#overview" className="text-gray-700 hover:text-red-600 transition-colors flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-red-500"></i>
                  <span>1. Tác giả và hoàn cảnh sáng tác</span>
                </a>
              </li>
              <li>
                <a href="#theme" className="text-gray-700 hover:text-red-600 transition-colors flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-red-500"></i>
                  <span>2. Chủ đề và ý nghĩa tác phẩm</span>
                </a>
              </li>
              <li>
                <a href="#structure" className="text-gray-700 hover:text-red-600 transition-colors flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-red-500"></i>
                  <span>3. Phân tích cấu trúc và nội dung từng đoạn</span>
                </a>
              </li>
              <li>
                <a href="#artistic" className="text-gray-700 hover:text-red-600 transition-colors flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-red-500"></i>
                  <span>4. Nghệ thuật biểu hiện</span>
                </a>
              </li>
              <li>
                <a href="#value" className="text-gray-700 hover:text-red-600 transition-colors flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-red-500"></i>
                  <span>5. Giá trị nhân văn và hiện thực</span>
                </a>
              </li>
              <li>
                <a href="#summary" className="text-gray-700 hover:text-red-600 transition-colors flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-red-500"></i>
                  <span>6. Tổng kết và ghi nhớ</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Section 1: Tác giả và hoàn cảnh */}
          <section id="overview" className="mb-12 scroll-mt-24">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
              1. Tác Giả Và Hoàn Cảnh Sáng Tác
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <h3 className="text-xl font-bold text-gray-900 mb-4">1.1. Về tác giả Tố Hữu</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Tố Hữu</strong> (1920-2002), tên thật là <strong>Nguyễn Kim Thành</strong>, là một trong những nhà thơ lớn nhất của nền văn học Việt Nam hiện đại. Ông sinh ra tại Huế, trong một gia đình quan lại nhà Nguyễn. Từ nhỏ, Tố Hữu đã tiếp xúc với nền văn hóa truyền thống sâu sắc của cố đô, nhưng cũng chứng kiến những bất công và đau khổ của dân tộc dưới ách thống trị thực dân.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Năm 1937, khi mới 17 tuổi, Tố Hữu gia nhập Đảng Cộng sản Đông Dương. Từ đó, thơ ông trở thành tiếng nói của cách mạng, của khát vọng độc lập tự do dân tộc. Thơ Tố Hữu gắn liền với các thời kỳ lịch sử quan trọng của dân tộc: kháng chiến chống Pháp, kháng chiến chống Mỹ, và thời kỳ xây dựng đất nước.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">1.2. Hoàn cảnh sáng tác bài thơ Việt Bắc</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bài thơ <strong>"Việt Bắc"</strong> được Tố Hữu sáng tác vào năm 1954, trong những ngày cuối cùng của cuộc kháng chiến chống thực dân Pháp. Đây là thời điểm mà toàn dân tộc đang hướng về Điện Biên Phủ, nơi diễn ra trận quyết chiến lịch sử.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Việt Bắc - vùng đất chiến khu cách mạng - là nơi đã nuôi dưỡng, che chở cho Trung ương Đảng, Chính phủ và Bác Hồ suốt 9 năm kháng chiến. Đây là mảnh đất anh hùng, nơi đã góp phần quan trọng vào thắng lợi vĩ đại của dân tộc. Tố Hữu đã từng sống và chiến đấu ở Việt Bắc, nên ông hiểu sâu sắc về vùng đất này, về con người nơi đây.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bài thơ được sáng tác trong tâm trạng vừa xúc động, vừa tự hào khi nhìn lại chặng đường đấu tranh gian khổ nhưng vẻ vang. Đây là lời tri ân sâu sắc của người chiến sĩ cách mạng đối với vùng đất và con người đã nuôi dưỡng, bảo vệ họ trong những năm tháng khó khăn nhất.
              </p>
            </div>
          </section>

          {/* Section 2: Chủ đề */}
          <section id="theme" className="mb-12 scroll-mt-24">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
              2. Chủ Đề Và Ý Nghĩa Tác Phẩm
            </h2>
            
            <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-xl mb-6">
              <p className="text-gray-800 font-medium leading-relaxed">
                <strong>Chủ đề:</strong> Bài thơ ca ngợi vẻ đẹp thiên nhiên, con người và truyền thống cách mạng của Việt Bắc - vùng đất anh hùng đã nuôi dưỡng cách mạng trong những năm kháng chiến chống Pháp.
              </p>
            </div>

            <div className="prose prose-lg max-w-none">
              <h3 className="text-xl font-bold text-gray-900 mb-4">2.1. Tình yêu quê hương đất nước</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Trước hết, "Việt Bắc" là một bài ca tình yêu quê hương sâu sắc. Tố Hữu không chỉ miêu tả vẻ đẹp thiên nhiên hùng vĩ của núi rừng Việt Bắc, mà còn thể hiện tình cảm gắn bó mật thiết của người chiến sĩ cách mạng với mảnh đất đã cho họ chỗ nương tựa, bảo vệ họ trong những năm tháng gian khổ.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-6">2.2. Lòng biết ơn sâu sắc</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bài thơ thấm đẫm lòng biết ơn của tác giả và của cả dân tộc đối với Việt Bắc. Đây là vùng đất đã cống hiến, hy sinh nhiều nhất cho sự nghiệp cách mạng. Nhân dân Việt Bắc đã nuôi quân, che giấu cán bộ, cung cấp lương thực, vũ khí, góp phần quyết định vào thắng lợi của cuộc kháng chiến.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-6">2.3. Khát vọng hòa bình</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Được viết vào thời điểm chiến tranh sắp kết thúc, bài thơ còn thể hiện khát vọng hòa bình, mong muốn được trở về với cuộc sống bình yên. Đây là khát vọng chung của toàn dân tộc sau gần một thập kỷ chiến tranh gian khổ.
              </p>
            </div>
          </section>

          {/* Section 3: Phân tích cấu trúc */}
          <section id="structure" className="mb-12 scroll-mt-24">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
              3. Phân Tích Cấu Trúc Và Nội Dung
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <h3 className="text-xl font-bold text-gray-900 mb-4">3.1. Đoạn 1: Khung cảnh thiên nhiên Việt Bắc</h3>
              <div className="bg-gray-50 p-6 rounded-xl mb-4 border border-gray-200">
                <p className="italic text-gray-700 leading-relaxed">
                  "Tôi về thăm lại đất Việt Bắc<br/>
                  Tôi về với đồng bào Việt Bắc..."
                </p>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Ngay từ câu mở đầu, Tố Hữu đã thể hiện tình cảm gắn bó sâu sắc với Việt Bắc qua việc lặp lại cụm từ <strong>"Tôi về"</strong>. Động từ "về" mang ý nghĩa về với quê hương, về với những gì thân thương, gắn bó. Việc lặp lại tạo nên sự nhấn mạnh, thể hiện sự háo hức, phấn khởi và xúc động của người đi về.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Tác giả miêu tả Việt Bắc với những hình ảnh thiên nhiên hùng vĩ, đặc trưng: núi cao, rừng rậm, suối trong. Đây không chỉ là khung cảnh tự nhiên mà còn là biểu tượng cho sự vững chãi, che chở, bảo vệ của vùng đất chiến khu.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">3.2. Đoạn 2: Con người Việt Bắc</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Tố Hữu dành nhiều dòng thơ để ca ngợi con người Việt Bắc - những người đã cưu mang, bảo vệ cách mạng. Họ được miêu tả với những phẩm chất cao đẹp: cần cù, dũng cảm, thủy chung với cách mạng. Dù cuộc sống gian khổ, thiếu thốn, nhưng họ luôn sẵn sàng chia sẻ, cống hiến cho sự nghiệp chung.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">3.3. Đoạn 3: Ký ức chiến đấu</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Phần này gợi nhớ lại những ngày tháng chiến đấu gian khổ nhưng đầy ý nghĩa. Tố Hữu nhắc đến những đêm họp Đảng, những cuộc hành quân trong rừng sâu, những trận đánh ác liệt. Tất cả tạo nên một bức tranh sống động về cuộc sống chiến đấu của những người cách mạng.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">3.4. Đoạn cuối: Khát vọng tương lai</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bài thơ kết thúc với những dòng tràn đầy hy vọng về tương lai. Sau những năm tháng chiến tranh gian khổ, đất nước sắp được hòa bình. Việt Bắc - vùng đất đã nuôi dưỡng cách mạng - sẽ được xây dựng phát triển, con người nơi đây sẽ được sống trong hòa bình, hạnh phúc.
              </p>
            </div>
          </section>

          {/* Section 4: Nghệ thuật */}
          <section id="artistic" className="mb-12 scroll-mt-24">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
              4. Nghệ Thuật Biểu Hiện
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <h3 className="text-xl font-bold text-gray-900 mb-4">4.1. Ngôn ngữ thơ</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Tố Hữu sử dụng ngôn ngữ giản dị, gần gũi nhưng đầy cảm xúc. Thơ ông không cầu kỳ, hoa mỹ mà chân thành, chất phác như chính con người Việt Bắc mà ông ca ngợi. Các từ ngữ được chọn lọc kỹ càng, mang đậm tính dân tộc và đặc trưng của vùng miền.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-6">4.2. Biện pháp nghệ thuật</h3>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-red-50 to-pink-50 p-5 rounded-xl border-l-4 border-red-600">
                  <h4 className="font-bold text-gray-900 mb-2">• Điệp từ, điệp ngữ:</h4>
                  <p className="text-gray-700">
                    Việc lặp lại "Tôi về", "Việt Bắc" tạo nhịp điệu và nhấn mạnh tình cảm. Kỹ thuật này giúp bài thơ dễ nhớ, dễ đọc và tăng tính trữ tình.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-red-50 to-pink-50 p-5 rounded-xl border-l-4 border-red-600">
                  <h4 className="font-bold text-gray-900 mb-2">• So sánh, ẩn dụ:</h4>
                  <p className="text-gray-700">
                    Tố Hữu khéo léo sử dụng các hình ảnh so sánh để làm nổi bật vẻ đẹp của Việt Bắc. Núi rừng được ví như bức tường thành vững chãi, suối nước như dòng máu trong lành của đất nước.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-red-50 to-pink-50 p-5 rounded-xl border-l-4 border-red-600">
                  <h4 className="font-bold text-gray-900 mb-2">• Nhân hóa:</h4>
                  <p className="text-gray-700">
                    Thiên nhiên Việt Bắc được nhân hóa, trở thành người bạn, người mẹ ấm áp che chở. Núi "đón", rừng "ôm", tạo nên cảm giác gần gũi, thân thương.
                  </p>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">4.3. Cấu trúc và thể thơ</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bài thơ được viết theo thể thơ tự do, không bị ràng buộc bởi luật thơ cổ điển. Điều này giúp tác giả tự do bày tỏ cảm xúc, diễn đạt ý tưởng một cách trọn vẹn nhất. Tuy nhiên, thơ vẫn có nhịp điệu nội tại, tạo nên sự du dương, dễ đọc, dễ thuộc.
              </p>
            </div>
          </section>

          {/* Section 5: Giá trị */}
          <section id="value" className="mb-12 scroll-mt-24">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
              5. Giá Trị Nhân Văn Và Hiện Thực
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <h3 className="text-xl font-bold text-gray-900 mb-4">5.1. Giá trị lịch sử</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                "Việt Bắc" là một tài liệu lịch sử sống động về cuộc kháng chiến chống Pháp. Qua bài thơ, thế hệ hôm nay có thể hiểu được phần nào về cuộc sống, tinh thần chiến đấu của thế hệ cha anh đã hy sinh vì độc lập tự do của Tổ quốc.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-6">5.2. Giá trị nhân văn</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bài thơ khẳng định giá trị cao đẹp của tình yêu quê hương, lòng biết ơn, sự hy sinh vì cộng đồng. Đây là những giá trị nhân văn vượt thời gian, luôn cần thiết trong mọi thời đại. Tố Hữu đã thành công trong việc nâng tầm những con người bình thường thành những người hùng trong lòng dân tộc.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4 mt-6">5.3. Giá trị nghệ thuật</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                "Việt Bắc" là một trong những tác phẩm tiêu biểu nhất của thơ ca cách mạng Việt Nam. Bài thơ thành công trong việc kết hợp nội dung cách mạng với hình thức nghệ thuật tinh tế. Ngôn ngữ giản dị nhưng giàu hình ảnh, cảm xúc chân thành nhưng không rơi vào sự sến súa, là những điểm mạnh của tác phẩm.
              </p>
            </div>
          </section>

          {/* Section 6: Tổng kết */}
          <section id="summary" className="mb-12 scroll-mt-24">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
              6. Tổng Kết Và Ghi Nhớ
            </h2>
            
            <div className="bg-gradient-to-br from-red-600 to-pink-600 text-white rounded-2xl p-8 mb-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <i className="ri-bookmark-line"></i>
                Những điểm cần ghi nhớ
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-double-line text-2xl flex-shrink-0 mt-0.5"></i>
                  <span><strong>Chủ đề:</strong> Ca ngợi Việt Bắc - vùng đất anh hùng đã nuôi dưỡng cách mạng trong kháng chiến chống Pháp</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-double-line text-2xl flex-shrink-0 mt-0.5"></i>
                  <span><strong>Cảm xúc chủ đạo:</strong> Tình yêu quê hương, lòng biết ơn sâu sắc, khát vọng hòa bình</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-double-line text-2xl flex-shrink-0 mt-0.5"></i>
                  <span><strong>Nghệ thuật:</strong> Ngôn ngữ giản dị, điệp từ, so sánh, nhân hóa, thơ tự do</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-double-line text-2xl flex-shrink-0 mt-0.5"></i>
                  <span><strong>Giá trị:</strong> Tài liệu lịch sử, giá trị nhân văn cao đẹp, nghệ thuật tinh tế</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-lightbulb-line text-amber-600"></i>
                Mẹo làm bài văn phân tích
              </h3>
              <ul className="space-y-2.5 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span>Nêu rõ hoàn cảnh lịch sử để làm nổi bật ý nghĩa của tác phẩm</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span>Phân tích chi tiết các đoạn thơ với dẫn chứng cụ thể</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span>Nêu rõ các biện pháp nghệ thuật và tác dụng của chúng</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-amber-600 flex-shrink-0 mt-1"></i>
                  <span>Đánh giá giá trị và ý nghĩa thời đại của tác phẩm</span>
                </li>
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-600"></i>
                Lỗi thường gặp khi phân tích
              </h3>
              <ul className="space-y-2.5 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-600 flex-shrink-0 mt-1"></i>
                  <span>Phân tích chung chung, không dẫn chứng cụ thể từ bài thơ</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-600 flex-shrink-0 mt-1"></i>
                  <span>Liệt kê biện pháp nghệ thuật mà không nói rõ tác dụng</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-600 flex-shrink-0 mt-1"></i>
                  <span>Không làm rõ được mối quan hệ giữa nội dung và nghệ thuật</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-600 flex-shrink-0 mt-1"></i>
                  <span>Đánh giá chủ quan, thiếu căn cứ về giá trị tác phẩm</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Related Lessons */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Bài học liên quan</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link to="/van/nghi-luan-xa-hoi" className="group bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl hover:shadow-lg transition-all border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i className="ri-article-line text-white text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1 group-hover:text-red-600 transition-colors">Nghị luận xã hội</h4>
                    <p className="text-sm text-gray-600">Cách viết bài văn nghị luận xã hội hiệu quả</p>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all"></i>
                </div>
              </Link>

              <Link to="/van/chiec-luoc-nga" className="group bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl hover:shadow-lg transition-all border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i className="ri-book-2-line text-white text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1 group-hover:text-red-600 transition-colors">Chiếc lược ngà</h4>
                    <p className="text-sm text-gray-600">Phân tích truyện ngắn Nguyễn Quang Sáng</p>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all"></i>
                </div>
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default VietBacLessonPage;
